/* Inline expandable card quick options — variant list that adds directly to cart. */

if (!customElements.get('card-quick-options')) {
  class CardQuickOptions extends HTMLElement {
    constructor() {
      super();
      this.trigger = this.querySelector('.card-quick-options__trigger');
      this.panel = this.querySelector('.card-quick-options__panel');
      this.form = this.querySelector('[data-card-quick-options-form]');
      this.variantInput = this.querySelector('[data-variant-input]');
      this.errorEl = this.querySelector('[data-card-quick-options-error]');
      this.cardWrapper =
        this.closest('.card-wrapper') ||
        this.closest('.product-card-minimal') ||
        this.closest('.complementary-product-card');
      this.addBtn = this.panel ? this.panel.querySelector('[data-quick-add]') : null;
      this.buyBtn = this.panel ? this.panel.querySelector('[data-quick-buy]') : null;
      this.selectedVariantId = null;
      this.panelHome = null;
      this.backdrop = null;
      this.isOpen = false;
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
    }

    connectedCallback() {
      if (!this.trigger || !this.panel || !this.form) return;
      this.trigger.addEventListener('click', this.toggle.bind(this));
      this.form.addEventListener('click', this.onOptionClick.bind(this));
      if (this.addBtn) {
        this.addBtn.addEventListener('click', () => this.onSheetAction(false, this.addBtn));
      }
      if (this.buyBtn) {
        this.buyBtn.addEventListener('click', () => this.onSheetAction(true, this.buyBtn));
      }
      this.relocatePanel();
    }

    /** True while the panel is presented as the mobile bottom sheet. */
    isSheetMode() {
      return this.panel.classList.contains('card-quick-options__panel--sheet');
    }

    /**
     * Move the panel out of the small "+" cell and into the card image
     * container so it can slide up from the bottom of the image instead
     * of from the bottom of the entire card-wrapper.
     */
    relocatePanel() {
      if (this.closest('.complementary-product-card')) return;

      const panelTarget =
        this.cardWrapper?.querySelector('.card__inner') ||
        this.cardWrapper?.querySelector('.product-card-minimal__media') ||
        this.cardWrapper?.querySelector('.complementary-product-card__media');

      if (!panelTarget) return;

      // Move the product-navigation overlay link into the same container as
      // the panel. The image container is a stacking context (transform on
      // .card__inner.gradient), so while the link lives outside it the panel
      // can never sit above the link and chip clicks fall through to product
      // navigation. Sharing the context lets the panel's higher z-index win in
      // its own region, while the rest of the image still links to the product.
      const overlayLink = this.cardWrapper?.querySelector('.card__media-overlay-link');
      if (overlayLink && overlayLink.parentElement !== panelTarget) {
        panelTarget.insertBefore(overlayLink, panelTarget.firstChild);
      }

      if (this.panel && this.panel.parentElement !== panelTarget) {
        panelTarget.appendChild(this.panel);
        // Drop the SSR `hidden` guard so the panel can be revealed on hover
        // (desktop) purely via CSS while staying invisible until then through
        // transform / opacity / visibility.
        this.panel.hidden = false;
      }

      // Move the "+" trigger over the product image as well, so on mobile it
      // sits at the bottom-right of the image (desktop hides it and reveals
      // the options on hover instead).
      if (this.trigger && this.trigger.parentElement !== panelTarget) {
        panelTarget.appendChild(this.trigger);
      }
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick, true);
      document.removeEventListener('keydown', this.onKeydown);
    }

    toggle(event) {
      if (event) event.preventDefault();
      if (this.isOpen) this.close();
      else this.open();
    }

    open() {
      if (this.isOpen) return;
      this.closeOthers();
      this.isOpen = true;
      this.panel.hidden = false;

      // Promote the panel to a viewport-level bottom sheet. It must leave the
      // card image container because that ancestor is transformed, which would
      // otherwise trap position:fixed inside the card instead of the viewport.
      this.panelHome = this.panel.parentElement;
      this.panel.classList.add('card-quick-options__panel--sheet');
      this.showBackdrop();
      document.body.appendChild(this.panel);
      document.body.classList.add('card-quick-options-sheet-open');
      requestAnimationFrame(() => {
        this.panel.setAttribute('data-open', 'true');
      });

      this.trigger.setAttribute('aria-expanded', 'true');
      document.addEventListener('click', this.onDocumentClick, true);
      document.addEventListener('keydown', this.onKeydown);
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.panel.removeAttribute('data-open');
      this.trigger.setAttribute('aria-expanded', 'false');
      this.hideBackdrop();
      document.body.classList.remove('card-quick-options-sheet-open');
      this.clearSelection();
      this.clearError();
      document.removeEventListener('click', this.onDocumentClick, true);
      document.removeEventListener('keydown', this.onKeydown);

      // Return the panel to the card once the slide-down transition completes.
      const home = this.panelHome;
      window.setTimeout(() => {
        if (this.isOpen) return;
        this.panel.classList.remove('card-quick-options__panel--sheet');
        if (home && this.panel.parentElement !== home) {
          home.appendChild(this.panel);
        }
      }, 340);
    }

    showBackdrop() {
      if (!this.backdrop) {
        this.backdrop = document.createElement('div');
        this.backdrop.className = 'card-quick-options__backdrop';
        this.backdrop.addEventListener('click', () => this.close());
      }
      if (this.backdrop.parentElement !== document.body) {
        document.body.appendChild(this.backdrop);
      }
      requestAnimationFrame(() => this.backdrop.classList.add('is-visible'));
    }

    hideBackdrop() {
      if (this.backdrop) this.backdrop.classList.remove('is-visible');
    }

    /** Highlight the chosen size and enable the sheet actions. */
    selectOption(button, variantId) {
      this.selectedVariantId = variantId;
      this.panel.querySelectorAll('.card-quick-options__option.is-selected').forEach((node) => {
        node.classList.remove('is-selected');
        node.setAttribute('aria-pressed', 'false');
      });
      button.classList.add('is-selected');
      button.setAttribute('aria-pressed', 'true');
      this.setSheetActionsEnabled(true);
      this.clearError();
    }

    clearSelection() {
      this.selectedVariantId = null;
      this.panel.querySelectorAll('.card-quick-options__option.is-selected').forEach((node) => {
        node.classList.remove('is-selected');
        node.setAttribute('aria-pressed', 'false');
      });
      this.setSheetActionsEnabled(false);
    }

    setSheetActionsEnabled(enabled) {
      [this.addBtn, this.buyBtn].forEach((btn) => {
        if (btn) btn.disabled = !enabled;
      });
    }

    onSheetAction(redirect, button) {
      if (!this.selectedVariantId) {
        this.showError(
          (window.variantStrings && window.variantStrings.unavailable) ||
            (window.accessibilityStrings && window.accessibilityStrings.error)
        );
        return;
      }
      this.submitVariant(this.selectedVariantId, button, { redirect });
    }

    closeOthers() {
      document.querySelectorAll('card-quick-options').forEach((node) => {
        if (node !== this && typeof node.close === 'function') node.close();
      });
    }

    onDocumentClick(event) {
      if (!this.isOpen) return;
      if (this.contains(event.target)) return;
      // The panel and trigger are relocated into the card image, so they
      // are no longer descendants of this element — check them explicitly.
      if (this.panel && this.panel.contains(event.target)) return;
      if (this.trigger && this.trigger.contains(event.target)) return;
      this.close();
    }

    onKeydown(event) {
      if (event.key === 'Escape' && this.isOpen) {
        event.stopPropagation();
        this.close();
        this.trigger.focus();
      }
    }

    onOptionClick(event) {
      const button = event.target.closest('.card-quick-options__option');
      if (!button) return;
      event.preventDefault();
      if (button.disabled || button.getAttribute('aria-disabled') === 'true') return;
      const variantId = button.dataset.variantId;
      if (!variantId) return;
      // Mobile sheet: tapping a size selects it (confirm via the action
      // buttons). Desktop hover: tapping a chip adds straight to the cart.
      if (this.isSheetMode()) {
        this.selectOption(button, variantId);
      } else {
        this.submitVariant(variantId, button);
      }
    }

    submitVariant(variantId, button, options) {
      const redirect = options && options.redirect;
      this.clearError();
      this.variantInput.value = variantId;
      this.trigger.classList.add('loading');
      button.classList.add('loading');
      const spinner = this.trigger.querySelector('.loading__spinner');
      if (spinner) spinner.classList.remove('hidden');
      // Show a loader inside the clicked chip itself.
      const buttonSpinner = button.querySelector('.loading__spinner');
      if (buttonSpinner) buttonSpinner.classList.remove('hidden');

      const cartTarget = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const sectionsToBundle = [];
      if (cartTarget && typeof cartTarget.getSectionsToRender === 'function') {
        cartTarget.getSectionsToRender().forEach((section) => {
          const sectionKey = section.section || section.id;
          if (sectionKey) sectionsToBundle.push(sectionKey);
        });
      }

      const formData = new FormData(this.form);
      if (sectionsToBundle.length) {
        formData.append('sections', sectionsToBundle.join(','));
        formData.append('sections_url', window.location.pathname);
      }
      if (cartTarget && typeof cartTarget.setActiveElement === 'function') {
        cartTarget.setActiveElement(this.trigger);
      }

      const config = {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: formData,
      };

      const cartAddUrl =
        (window.routes && window.routes.cart_add_url) ||
        (typeof routes !== 'undefined' && routes.cart_add_url) ||
        '/cart/add';

      fetch(`${cartAddUrl}.js`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            this.showError(response.description || response.message || response.errors || 'Error');
            return;
          }
          // Buy now: skip the cart drawer and go straight to checkout.
          if (redirect) {
            window.location.href = `${window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/'}checkout`.replace(
              '//checkout',
              '/checkout'
            );
            return;
          }
          if (cartTarget && typeof cartTarget.renderContents === 'function') {
            cartTarget.renderContents(response);
          } else {
            this.refreshCartCount();
          }
          try {
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'card-quick-options',
                productVariantId: variantId,
                cartData: response,
              });
            }
          } catch (_) {
            /* publish not available — cart target re-render is enough */
          }
          this.close();
        })
        .catch(() => {
          const message =
            (window.accessibilityStrings && window.accessibilityStrings.networkError) ||
            (window.accessibilityStrings && window.accessibilityStrings.error);
          this.showError(message);
        })
        .finally(() => {
          this.trigger.classList.remove('loading');
          button.classList.remove('loading');
          if (spinner) spinner.classList.add('hidden');
          if (buttonSpinner) buttonSpinner.classList.add('hidden');
        });
    }

    refreshCartCount() {
      const cartUrl =
        (window.routes && window.routes.cart_url) ||
        (typeof routes !== 'undefined' && routes.cart_url) ||
        '/cart';
      fetch(`${cartUrl}.js`)
        .then((response) => response.json())
        .then((cart) => {
          document.querySelectorAll('.cart-count-bubble').forEach((bubble) => {
            const counter = bubble.querySelector('span[aria-hidden="true"]');
            if (counter) counter.textContent = cart.item_count;
          });
        })
        .catch(() => {});
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent =
        typeof message === 'string'
          ? message
          : (window.accessibilityStrings && window.accessibilityStrings.error) || '';
      this.errorEl.hidden = false;
    }

    clearError() {
      if (!this.errorEl) return;
      this.errorEl.textContent = '';
      this.errorEl.hidden = true;
    }
  }

  customElements.define('card-quick-options', CardQuickOptions);
}
